import React, { useState } from 'react';

import { observer } from 'mobx-react';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';

import classes from './MacroPlanStep.module.scss';
import { ButtonsRow } from '../../components/ButtonsRow/ButtonsRow';
import { customerSetupStore } from '../../../../stores/customerSetupStore';
import {
  LbTabPanel,
  LbTabs,
  LbVerticalPageTabsList,
  TabsConfig,
} from '../../../../../shared/components/tabs';
import {
  MealPlanTable,
  MealPlansMacrosFormValues,
  WorkoutMealsTable,
  mealPlanService,
} from '../../../../../nutrition';

type MacroPlanStepProps = {
  customerId: number;
};

const MacroPlanStep = observer(({ customerId }: MacroPlanStepProps) => {
  const { stepperMethods } = customerSetupStore;
  const [activeTab, setActiveTab] = useState<number>(2);

  const formMethods = useForm<MealPlansMacrosFormValues>({
    mode: 'onBlur',
    defaultValues: async () => mealPlanService.getMealPlansMacros(customerId),
  });
  const {
    control,
    handleSubmit,
    trigger,
    formState: { isSubmitting },
  } = formMethods;

  const { fields } = useFieldArray({ control, name: 'mealPlans' });

  const handleFormSubmit = handleSubmit(async (macroPlan) => {
    await mealPlanService.updateMealPlansMacros(customerId, {
      ...macroPlan,
      isImmediately: true,
    });

    stepperMethods.goToNextStep();
  });

  const handleChangeTab = async (newValue: number) => {
    const isValid = await trigger();

    if (isValid) {
      setActiveTab(Number(newValue));
    }
  };

  const workoutTabConfig: TabsConfig = {
    label: 'Pre/Post Workout',
    value: 1,
    panel: <WorkoutMealsTable isEditMode />,
  };

  const mealPlanTabsConfig: TabsConfig[] = fields.map((mealPlan, index) => ({
    label: mealPlan.originName,
    value: index + 2, // +2 because for workout set value 1, (tabs defaultValue 2 show initially meal plan tab)
    panel: <MealPlanTable nameIndex={index} isEditMode />,
  }));

  const tabsConfigs = [workoutTabConfig, ...mealPlanTabsConfig];

  return (
    <div>
      <FormProvider {...formMethods}>
        <LbTabs
          value={activeTab}
          onValueChange={handleChangeTab}
          className={classes.tabsBox}
        >
          <LbVerticalPageTabsList tabsConfig={tabsConfigs} />
          <LbTabPanel tabsConfig={tabsConfigs} />
        </LbTabs>
      </FormProvider>
      <ButtonsRow onNext={handleFormSubmit} yesDisabled={isSubmitting} />
    </div>
  );
});

export { MacroPlanStep };
